import React, { useCallback, useMemo, useState } from 'react';
import { ArrowUpIcon, ArrowDownIcon, ArrowUpDownIcon, ChevronDownIcon, ChevronsDownIcon, ChevronsUpIcon, LayoutGridIcon } from 'lucide-react';
import { useConditions } from '../hooks/useConditions';
import { FilterBar } from '../components/FilterBar';
import { GroupSection } from '../components/GroupSection';
import { BulkActionsToolbar } from '../components/BulkActionsToolbar';
import { AddConditionModal } from '../components/AddConditionModal';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import { ConditionStatus, CATEGORIES, STATUSES, ASSIGNEES, Condition, Document } from '../types/condition';
import { ConditionRow } from '../components/ConditionRow';
type ConditionTableProps = {
  onViewModeChange?: (mode: 'table' | 'board') => void;
};
type GroupBy = 'category' | 'status' | 'assignee' | 'none';
export function ConditionTable({
  onViewModeChange
}: ConditionTableProps) {
  const {
    conditions,
    conditionsByCategory,
    searchQuery,
    setSearchQuery,
    activeCategories,
    toggleCategory,
    activeAssignees,
    toggleAssignee,
    setAllAssignees,
    activeStatuses,
    toggleStatus,
    setAllStatuses,
    sortField,
    sortDirection,
    handleSort,
    addCondition,
    updateCondition,
    updateConditionTitle,
    updateConditionDueDate,
    deleteCondition,
    totalCount,
    selectedIds,
    toggleSelection,
    selectAll,
    clearSelection,
    bulkUpdateStatus,
    bulkDelete,
    addDocument,
    deleteDocument,
    downloadAllDocuments
  } = useConditions();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupBy>('category');
  const [isGroupByOpen, setIsGroupByOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [allGroupsExpanded, setAllGroupsExpanded] = useState(true);
  const handleUpdateStatus = useCallback((id: string, status: ConditionStatus) => {
    updateCondition(id, {
      status
    });
  }, [updateCondition]);
  const handleUpdateAssignee = useCallback((id: string, assignee: string) => {
    updateCondition(id, {
      assignee
    });
  }, [updateCondition]);
  const handleBulkUpdateStatus = useCallback((status: ConditionStatus) => {
    bulkUpdateStatus(Array.from(selectedIds), status);
  }, [bulkUpdateStatus, selectedIds]);
  const handleBulkDelete = useCallback(() => {
    bulkDelete(Array.from(selectedIds));
  }, [bulkDelete, selectedIds]);
  const handlePreviewDocument = useCallback((document: Document) => {
    setPreviewDocument(document);
    setIsPreviewOpen(true);
  }, []);
  const groupedConditions = useMemo(() => {
    if (groupBy === 'category') {
      return conditionsByCategory;
    } else if (groupBy === 'status') {
      const grouped: Record<string, Condition[]> = {};
      STATUSES.forEach(status => {
        grouped[status] = conditions.filter(c => c.status === status);
      });
      return grouped;
    } else if (groupBy === 'assignee') {
      const grouped: Record<string, Condition[]> = {};
      ASSIGNEES.forEach(assignee => {
        grouped[assignee] = conditions.filter(c => c.assignee === assignee);
      });
      return grouped;
    } else {
      return {
        'All Conditions': conditions
      };
    }
  }, [groupBy, conditions, conditionsByCategory]);
  const pendingCount = conditions.filter(c => c.status === 'Pending').length;
  const inProgressCount = conditions.filter(c => c.status === 'In Progress').length;
  const approvedCount = conditions.filter(c => c.status === 'Approved').length;
  const waivedCount = conditions.filter(c => c.status === 'Waived').length;
  const completionRate = totalCount > 0 ? Math.round((approvedCount + waivedCount) / totalCount * 100) : 0;
  const groupByLabels: Record<GroupBy, string> = {
    category: 'Category',
    status: 'Status',
    assignee: 'Assignee',
    none: 'None (Flat List)'
  };
  return <div className="min-h-screen bg-slate-50 flex flex-col pb-20" data-id="element-382">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm" data-id="element-383">
        <div className="px-6 py-5" data-id="element-384">
          <div className="flex items-start justify-between" data-id="element-385">
            {/* Left: Title and Loan Info */}
            <div data-id="element-386">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-387">
                Condition Tracker
              </h1>
              <p className="text-sm text-slate-500 mt-1" data-id="element-388">
                Loan #2024-001234 • Johnson, Michael & Sarah
              </p>
            </div>

            {/* Right: View Toggle */}
            {onViewModeChange && <button onClick={() => onViewModeChange('board')} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all" data-id="element-389">
                <LayoutGridIcon className="w-4 h-4" data-id="element-390" />
                Board View
              </button>}
          </div>

          {/* Stats Row */}
          <div className="flex items-center gap-1 mt-5" data-id="element-391">
            <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg" data-id="element-392">
              <div className="w-2 h-2 rounded-full bg-slate-400" data-id="element-393"></div>
              <div data-id="element-394">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide" data-id="element-395">
                  Pending
                </p>
                <p className="text-xl font-semibold text-slate-700" data-id="element-396">
                  {pendingCount}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 rounded-lg" data-id="element-397">
              <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-398"></div>
              <div data-id="element-399">
                <p className="text-xs font-medium text-blue-600 uppercase tracking-wide" data-id="element-400">
                  In Progress
                </p>
                <p className="text-xl font-semibold text-blue-700" data-id="element-401">
                  {inProgressCount}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 rounded-lg" data-id="element-402">
              <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-403"></div>
              <div data-id="element-404">
                <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide" data-id="element-405">
                  Approved
                </p>
                <p className="text-xl font-semibold text-emerald-700" data-id="element-406">
                  {approvedCount}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-3 bg-purple-50 rounded-lg" data-id="element-407">
              <div className="w-2 h-2 rounded-full bg-purple-500" data-id="element-408"></div>
              <div data-id="element-409">
                <p className="text-xs font-medium text-purple-600 uppercase tracking-wide" data-id="element-410">
                  Complete
                </p>
                <p className="text-xl font-semibold text-purple-700" data-id="element-411">
                  {completionRate}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Filter Bar */}
      <FilterBar searchQuery={searchQuery} onSearchChange={setSearchQuery} activeCategories={activeCategories} onToggleCategory={toggleCategory} activeAssignees={activeAssignees} onToggleAssignee={toggleAssignee} onSetAllAssignees={setAllAssignees} activeStatuses={activeStatuses} onToggleStatus={toggleStatus} onSetAllStatuses={setAllStatuses} onAddClick={() => setIsModalOpen(true)} totalCount={totalCount} filteredCount={conditions.length} data-id="element-412" />

      {/* Sort Controls + Group By */}
      <div className="bg-white border-b border-slate-200 px-6 py-3" data-id="element-413">
        <div className="flex items-center justify-between" data-id="element-414">
          {/* Sort Controls */}
          <div className="flex items-center gap-1.5" data-id="element-415">
            <span className="text-sm text-slate-500 font-medium mr-1" data-id="element-416">
              Sort:
            </span>
            {(['title', 'dueDate', 'assignee', 'priority'] as const).map(field => <button key={field} onClick={() => handleSort(field)} className={`
                  px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 transition-all
                  ${sortField === field ? 'bg-blue-100 text-blue-700 font-medium' : 'text-slate-600 hover:bg-slate-100'}
                `} data-id="element-417">
                  {field === 'title' && 'Title'}
                  {field === 'dueDate' && 'Due Date'}
                  {field === 'assignee' && 'Assignee'}
                  {field === 'priority' && 'Priority'}
                  {sortField === field ? sortDirection === 'asc' ? <ArrowUpIcon className="w-3.5 h-3.5" data-id="element-418" /> : <ArrowDownIcon className="w-3.5 h-3.5" data-id="element-419" /> : <ArrowUpDownIcon className="w-3.5 h-3.5 opacity-40" data-id="element-420" />}
                </button>)}
          </div>

          {/* Group By + Expand/Collapse */}
          <div className="flex items-center gap-2" data-id="element-421">
            {groupBy !== 'none' && <button onClick={() => setAllGroupsExpanded(!allGroupsExpanded)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-md transition-all" data-id="element-422">
                {allGroupsExpanded ? <>
                    <ChevronsUpIcon className="w-4 h-4" data-id="element-423" />
                    Collapse All
                  </> : <>
                    <ChevronsDownIcon className="w-4 h-4" data-id="element-424" />
                    Expand All
                  </>}
              </button>}

            <div className="w-px h-6 bg-slate-200 mx-1" data-id="element-425"></div>

            <div className="relative" data-id="element-426">
              <button onClick={() => setIsGroupByOpen(!isGroupByOpen)} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-md transition-all" data-id="element-427">
                <span className="text-slate-500" data-id="element-428">Group:</span>
                <span data-id="element-429">{groupByLabels[groupBy]}</span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" data-id="element-430" />
              </button>

              {isGroupByOpen && <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsGroupByOpen(false)} data-id="element-431" />
                  <div className="absolute right-0 top-full mt-1 z-50 w-44 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden" data-id="element-432">
                    {(['category', 'status', 'assignee', 'none'] as GroupBy[]).map(option => <button key={option} onClick={() => {
                  setGroupBy(option);
                  setIsGroupByOpen(false);
                }} className={`
                          w-full px-4 py-2.5 text-sm text-left transition-colors
                          ${groupBy === option ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700 hover:bg-slate-50'}
                        `} data-id="element-433">
                        {groupByLabels[option]}
                      </button>)}
                  </div>
                </>}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 p-6" data-id="element-434">
        <div className="space-y-4" data-id="element-435">
          {groupBy === 'none' ? <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-436">
              <div className="overflow-x-auto" data-id="element-437">
                <table className="w-full" data-id="element-438">
                  <thead className="bg-slate-50 border-b border-slate-200" data-id="element-439">
                    <tr data-id="element-440">
                      <th className="px-4 py-3 w-12" data-id="element-441"></th>
                      <th className="px-4 py-3 w-12" data-id="element-442">
                        <input type="checkbox" checked={conditions.length > 0 && conditions.every(c => selectedIds.has(c.id))} onChange={() => {
                      if (conditions.every(c => selectedIds.has(c.id))) {
                        clearSelection();
                      } else {
                        selectAll();
                      }
                    }} className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" aria-label="Select all conditions" data-id="element-443" />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-444">
                        Condition
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-44" data-id="element-445">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-36" data-id="element-446">
                        Due Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-32" data-id="element-447">
                        Assignee
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-28" data-id="element-448">
                        Docs
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-20" data-id="element-449">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-450">
                    {conditions.map(condition => <ConditionRow key={condition.id} condition={condition} isSelected={selectedIds.has(condition.id)} onToggleSelect={toggleSelection} onUpdateStatus={handleUpdateStatus} onUpdateAssignee={handleUpdateAssignee} onUpdateTitle={updateConditionTitle} onUpdateDueDate={updateConditionDueDate} onDelete={deleteCondition} onUploadDocument={addDocument} onDeleteDocument={deleteDocument} onPreviewDocument={handlePreviewDocument} onDownloadAllDocuments={downloadAllDocuments} data-id="element-451" />)}
                  </tbody>
                </table>
              </div>
            </div> : Object.entries(groupedConditions).filter(([_, groupConditions]) => groupConditions.length > 0).map(([groupName, groupConditions]) => <GroupSection key={groupName} groupName={groupName} groupType={groupBy} conditions={groupConditions} selectedIds={selectedIds} onToggleSelect={toggleSelection} onSelectAll={selectAll} onUpdateStatus={handleUpdateStatus} onUpdateAssignee={handleUpdateAssignee} onUpdateTitle={updateConditionTitle} onUpdateDueDate={updateConditionDueDate} onDelete={deleteCondition} onUploadDocument={addDocument} onDeleteDocument={deleteDocument} onPreviewDocument={handlePreviewDocument} onDownloadAllDocuments={downloadAllDocuments} forceExpanded={allGroupsExpanded} data-id="element-452" />)}

          {conditions.length === 0 && <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center" data-id="element-453">
              <p className="text-slate-500 text-lg mb-4" data-id="element-454">
                No conditions match your filters
              </p>
              <button onClick={() => setIsModalOpen(true)} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors" data-id="element-455">
                Add Condition
              </button>
            </div>}
        </div>
      </main>

      <BulkActionsToolbar selectedCount={selectedIds.size} onClearSelection={clearSelection} onBulkUpdateStatus={handleBulkUpdateStatus} onBulkDelete={handleBulkDelete} data-id="element-456" />

      <AddConditionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAdd={addCondition} defaultStatus="Pending" data-id="element-457" />

      <DocumentPreviewModal document={previewDocument} isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} data-id="element-458" />
    </div>;
}